import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StatistikProduktivitetAfdelinger } from './StatistikProduktivitetAfdelinger';
import { StatistikProduktivitetMedarbejder } from '../../models/StatistikProduktivitetMedarbejder';
import { ExcelService } from '@store';
import { Observable } from 'rxjs';
import { ProduktivitetFormler } from '../../models/ProduktivitetFormler';
import { StatistikService } from '../../statistik.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StatistikProduktivitetEmployeeTimeLogModal } from '../statistik-produktivitet-employee-time-log-modal/statistik-produktivitet-employee-time-log-modal.component';
import {
    StatistikPlantidModalComponent
} from '../../tidsforbrug/statistik-plantid-modal/statistik-plantid-modal.component';
import { firstDayThisMonth, lastDayThisMonth } from '../../../shared/month-picker/month-picker.helper';
import { saveAs } from 'file-saver';

@Component({
    selector: 'app-statistik-produktivitet-ui',
    templateUrl: './statistik-produktivitet.component.html',
    styleUrls: ['./statistik-produktivitet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StatistikProduktivitetComponent implements OnInit {
    @Input() afdelinger: StatistikProduktivitetAfdelinger[];
    @Input() periode: {fra: string, til: string};
    @Output() periodeChange = new EventEmitter<{fra: string, til: string}>();

    expandedAfdelinger: string[] = [];
    selectedGruppeId: number;
    listOfFormels$: Observable<ProduktivitetFormler[]>;


    constructor(
        private statistikService: StatistikService,
        private modalService: NgbModal,
        private excel: ExcelService,
    ) { }

    ngOnInit(): void {
        const newPeriode = {
            fra: firstDayThisMonth(),
            til: lastDayThisMonth()
        };
        this.onPeriodeChange(newPeriode);
        this.listOfFormels$ = this.statistikService.getProduktivtetFormler();
    }

    get alleMedarbejdere() {
        return this.afdelinger?.flatMap(afdeling => afdeling.medarbejdere);
    }

    toggleAfdelinger() {
        if (this.expandedAfdelinger.length === 0) {
            this.expandedAfdelinger = this.afdelinger?.map(afdeling => afdeling.afdelingNavn);
            return;
        }
        this.expandedAfdelinger = [];
    }

    toggleAfdeling(afdeling: string) {
        if (this.expandedAfdelinger.includes(afdeling)) {
            this.collapseAfdeling(afdeling);
            return;
        }
        this.expandAfdeling(afdeling);
    }

    expandAfdeling(afdeling: string) {
        this.expandedAfdelinger = [...this.expandedAfdelinger, afdeling];
    }

    collapseAfdeling(afdeling: string) {
        this.expandedAfdelinger = this.expandedAfdelinger
            .filter(showAfdeling => afdeling !== showAfdeling);
    }

    onPeriodeChange(periode: { fra: string; til: string }) {
        this.afdelinger = [];
        this.periodeChange.emit(periode);
    }


    onSelectGruppe(id: number) {
        this.selectedGruppeId = id;
        for (const afdeling of this.afdelinger) {
            for (const medarbejder of afdeling.medarbejdere) {
                if (this.isMedarbejderInSelectedGruppe(medarbejder) || !id) {
                    medarbejder.excludeFromCalculation = false;
                } else {
                    medarbejder.excludeFromCalculation = true;
                }
            }
            afdeling.medarbejdere = [...afdeling.medarbejdere];
        }
    }

    isMedarbejderVisible(medarbejder: StatistikProduktivitetMedarbejder): boolean {
        if (!this.expandedAfdelinger.includes(medarbejder.afdeling) && this.afdelinger.length !== 1) {
            return false;
        }
        if (this.selectedGruppeId && !this.isMedarbejderInSelectedGruppe(medarbejder)) {
            return false;
        }
        if (medarbejder.normtid === 0
            && medarbejder.plan_tid_kunder === 0
            && medarbejder.total === 0
            && medarbejder.fakturebar === 0
            && medarbejder.intern_produktiv === 0
            && medarbejder.intern_uproduktiv === 0
            && medarbejder.sygdom === 0
            && medarbejder.ferie === 0
            && medarbejder.omregnet_overtid === 0
            && medarbejder.udbetalt_overtid === 0) {
            return false;
        }
        return true;
    }

    private isMedarbejderInSelectedGruppe(medarbejder: StatistikProduktivitetMedarbejder) {
        return medarbejder.grupper
            ?.split(',')
            .map(gruppe => +gruppe)
            .includes(this.selectedGruppeId);
    }

    isAfdelingVisible(afdeling: StatistikProduktivitetAfdelinger) {
        for (const medarbejder of afdeling.medarbejdere) {
            if (!medarbejder.excludeFromCalculation
                && !(medarbejder.normtid === 0
                && medarbejder.plan_tid_kunder === 0
                && medarbejder.total === 0
                && medarbejder.fakturebar === 0
                && medarbejder.intern_produktiv === 0
                && medarbejder.intern_uproduktiv === 0
                && medarbejder.sygdom === 0
                && medarbejder.ferie === 0
                && medarbejder.omregnet_overtid === 0
                && medarbejder.udbetalt_overtid === 0)) {
                return true;
            }
        }
        return false;
    }

    trackMedarbejderInit(_, medarbejder: StatistikProduktivitetMedarbejder) {
        return medarbejder.init;
    }

    trackAfdelingNavn(_, afdeling: StatistikProduktivitetAfdelinger) {
        return afdeling.afdelingNavn;
    }

    trackFormelId(_, formel: ProduktivitetFormler) {
        return formel.id;
    }

    calcFormula(medarbejder: StatistikProduktivitetMedarbejder, formel: string): { result: number, valid: boolean} {
        const vars = {
            A: medarbejder.normtid,
            B: medarbejder.plan_tid_kunder,
            C: medarbejder.total,
            D: medarbejder.fakturebar,
            E: medarbejder.intern_produktiv,
            F: medarbejder.intern_uproduktiv,
            G: medarbejder.sygdom,
            H: medarbejder.ferie,
            I: medarbejder.omregnet_overtid,
            J: medarbejder.udbetalt_overtid
        };

        if (formel) {
            const parsedFormula = formel.replace(/[A-J]/g, match => vars[match]);

            try {
                const result = new Function(`return ${parsedFormula}`)();
                const isFiniteNumber = Number.isFinite(result);

                if (isFiniteNumber) {
                    return { result: result, valid: true };
                }
            } catch (e) {
                console.log('Error parsing formula: ', formel);
            }
        }
        return { result: 0, valid: false };
    }

    CalcFormulaTotals(medarbejdere: StatistikProduktivitetMedarbejder[], formel: string): { result: number, valid: boolean } {
        let result = 0;
        if (medarbejdere) {
            medarbejdere.forEach(medarbejder => {
                const medarbejderResult = this.calcFormula(medarbejder, formel);
                if (medarbejderResult.valid && (this.isMedarbejderInSelectedGruppe(medarbejder) || this.selectedGruppeId == null)) {
                    if (typeof medarbejderResult.result === 'number') {
                        result += medarbejderResult.result;
                    }
                }
            });
        }

        if (formel) {
            try {
                if (Number.isFinite(result) && result !== 0) {
                    return { result: result, valid: true };
                }
            } catch (e) {
                console.log('Error parsing formula: ', formel);
            }

            return {result: 0, valid: false};
        }

        return {result: 0, valid: false};
    }

    async openEmployeeTimeLogModal(medarbejder: StatistikProduktivitetMedarbejder, gruppe: number) {
        const propertyMap = {
            2: 'intern_produktiv',
            3: 'intern_uproduktiv',
            4: 'sygdom',
            5: 'ferie',
            6: 'omregnet_overtid',
            7: 'udbetalt_overtid'
        };

        const property = propertyMap[gruppe];

        if (property && medarbejder[property] !== 0) {
            const employeeTimeLogModal = this.modalService.open(StatistikProduktivitetEmployeeTimeLogModal, {size: 'lg'});
            employeeTimeLogModal.componentInstance.medarbejder = medarbejder;
            employeeTimeLogModal.componentInstance.selectedPeriod = this.periode;
            employeeTimeLogModal.componentInstance.selectedGruppe = gruppe;
        }
    }

    async openPlantidModal(medarbejder: StatistikProduktivitetMedarbejder) {
        if (medarbejder.plan_tid_kunder !== 0) {
            const plantidModal = this.modalService.open(StatistikPlantidModalComponent, {size: 'lg'});
            plantidModal.componentInstance.medid = medarbejder.medID;
            plantidModal.componentInstance.medarbejderName = medarbejder.init;
            plantidModal.componentInstance.selectedPeriod = this.periode;
        }
    }

    onExport(listOfFormels: ProduktivitetFormler[]) {
        const exportArray = [
            ['Afdeling', 'Init', 'Normtid', 'Plan tid kunder (hele måneder)', 'Total', 'Fakturerbar', 'Intern produktiv', 'Intern uproduktiv', 'Sygdom', 'Ferie', 'Omregnet Overtid', 'Udbetalt omtid', ...listOfFormels.map(formel => formel.betegnelse)],
            ...this.afdelinger.flatMap(afdeling => [...afdeling.medarbejdere.map(medarbejder => [
                afdeling.afdelingNavn || '',
                medarbejder.init,
                medarbejder.normtid,
                medarbejder.plan_tid_kunder,
                medarbejder.total,
                medarbejder.fakturebar,
                medarbejder.intern_produktiv,
                medarbejder.intern_uproduktiv,
                medarbejder.sygdom,
                medarbejder.ferie,
                medarbejder.omregnet_overtid,
                medarbejder.udbetalt_overtid,
                ...listOfFormels.map(formel => this.calcFormula(medarbejder, formel.formel).result)
            ])])
        ];
        this.excel.generateExcel(exportArray).subscribe((blob: Blob) => {
            saveAs(blob, 'statistik-produktivitet.xlsx');
        });
    }
}
